import React, { useState, useEffect } from 'react'
import { X, List, Plus, Package, MapPin, Loader2 } from 'lucide-react'
import { Button } from './ui/Button'
import { Input } from './ui/Input'
import { OrangeSection } from './ui/OrangeSection'

interface PicklistSlideoutProps {
  isOpen: boolean
  onClose: () => void
  itemData?: {
    id: string
    name: string
    ic: string
    location: string
    tote: string
  }
  onPicklistComplete: (data: any) => void
}

export default function PicklistSlideout({
  isOpen,
  onClose,
  itemData,
  onPicklistComplete
}: PicklistSlideoutProps) {
  const [formData, setFormData] = useState({
    picklistName: '',
    priority: 'normal',
    notes: '',
    estimatedPickTime: '',
    assignedTo: ''
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [users, setUsers] = useState<any[]>([])

  // Fetch users on mount
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await fetch('/api/admin/users')
        if (response.ok) {
          const data = await response.json()
          setUsers(data.users || [])
        }
      } catch (error) {
        console.error('Failed to fetch users:', error)
      }
    }
    fetchUsers()
  }, [])

  // Reset form when slideout opens/closes
  useEffect(() => {
    if (!isOpen) {
      setFormData({
        picklistName: '',
        priority: 'normal',
        notes: '',
        estimatedPickTime: '',
        assignedTo: ''
      })
      setIsSubmitting(false)
    }
  }, [isOpen])

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)

    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000))
      
      const picklistData = {
        itemId: itemData?.id,
        itemIc: itemData?.ic,
        picklistName: formData.picklistName,
        priority: formData.priority,
        notes: formData.notes,
        estimatedPickTime: formData.estimatedPickTime,
        assignedTo: formData.assignedTo,
        status: 'pending',
        createdAt: new Date().toISOString(),
        timestamp: new Date().toISOString()
      }
      
      onPicklistComplete(picklistData)
      onClose()
    } catch (error) {
      console.error('Picklist creation failed:', error)
    } finally {
      setIsSubmitting(false)
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black bg-opacity-50"
        onClick={onClose}
      />
      
      {/* Slide-out panel */}
      <div className="absolute right-0 top-0 h-full w-96 bg-gray-900 border-4 border-orange-300 shadow-2xl shadow-orange-300/50 ring-2 ring-orange-200/20 transform transition-transform duration-300 ease-in-out">
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-700">
            <div className="flex items-center gap-2">
              <List className="h-5 w-5 text-white" />
              <h2 className="text-lg font-semibold text-white">Add to Pick List</h2>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-white"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-4">
            <OrangeSection>
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <Package className="h-5 w-5 text-white" />
                  <h3 className="text-lg font-medium text-white">Item Details</h3>
                </div>
                <div className="space-y-2">
                  <div>
                    <label className="block text-sm font-medium text-white mb-1">Item</label>
                    <Input
                      value={itemData?.name || ''}
                      className="bg-gray-800 text-white border-gray-600"
                      readOnly
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-white mb-1">IC</label>
                    <Input
                      value={itemData?.ic || ''}
                      className="bg-gray-800 text-white border-gray-600"
                      readOnly
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-white mb-1">Current Location</label>
                    <Input
                      value={itemData?.location || ''}
                      className="bg-gray-800 text-white border-gray-600"
                      readOnly
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-white mb-1">Current Tote</label>
                    <Input
                      value={itemData?.tote || ''}
                      className="bg-gray-800 text-white border-gray-600"
                      readOnly
                    />
                  </div>
                </div>
              </div>
            </OrangeSection>

            <OrangeSection>
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <List className="h-5 w-5 text-white" />
                  <h3 className="text-lg font-medium text-white">Pick List Details</h3>
                </div>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-white mb-2">Pick List Name</label>
                    <Input
                      value={formData.picklistName}
                      onChange={(e) => handleInputChange('picklistName', e.target.value)}
                      className="bg-gray-800 text-white border-gray-600"
                      placeholder="Enter pick list name"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-white mb-2">Priority</label>
                    <select
                      value={formData.priority}
                      onChange={(e) => handleInputChange('priority', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-600 rounded-md bg-gray-800 text-white"
                    >
                      <option value="low">Low</option>
                      <option value="normal">Normal</option>
                      <option value="high">High</option>
                      <option value="urgent">Urgent</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-white mb-2">Assigned To</label>
                    <select
                      value={formData.assignedTo}
                      onChange={(e) => handleInputChange('assignedTo', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-600 rounded-md bg-gray-800 text-white"
                    >
                      <option value="">Select Assignee</option>
                      {users.map((user) => (
                        <option key={user.id} value={user.email}>
                          {user.email}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-white mb-2">Estimated Pick Time (minutes)</label>
                    <Input
                      type="number"
                      value={formData.estimatedPickTime}
                      onChange={(e) => handleInputChange('estimatedPickTime', e.target.value)}
                      className="bg-gray-800 text-white border-gray-600"
                      placeholder="5"
                      min="1"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-white mb-2">Notes</label>
                    <textarea
                      value={formData.notes}
                      onChange={(e) => handleInputChange('notes', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-600 rounded-md bg-gray-800 text-white"
                      rows={3}
                      placeholder="Special instructions or notes for picking this item..."
                    />
                  </div>

                  <div className="flex gap-2 pt-4">
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={onClose}
                      className="flex-1"
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      disabled={isSubmitting}
                      className="flex-1 bg-orange-600 hover:bg-orange-700"
                    >
                      {isSubmitting ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Creating...
                        </>
                      ) : (
                        <>
                          <Plus className="h-4 w-4 mr-2" />
                          Add to Pick List
                        </>
                      )}
                    </Button>
                  </div>
                </form>
              </div>
            </OrangeSection>
          </div>
        </div>
      </div>
    </div>
  )
}
